import {clamp, normalizeCuts, retainedIntervals} from './editModel.js';

// Each source moment occurs at most once; order is independent of source time.
export const sequenceClips = project => project.clips ?? retainedIntervals(project.cuts, project.duration)
  .map(part => ({...part, id: `clip-${part.start}-${part.end}`}));
export const sequenceDuration = project => sequenceClips(project).reduce((sum, part) => sum + part.end - part.start, 0);

export function withSequence(project, clips) {
  const sourceOrder = [...clips].sort((a, b) => a.start - b.start);
  for (let i = 0; i < sourceOrder.length; i++) {
    const part = sourceOrder[i];
    if (!Number.isFinite(part.start) || !Number.isFinite(part.end) || part.start < 0 ||
      part.end > project.duration || part.end <= part.start || (i && part.start < sourceOrder[i - 1].end - 1e-8)) {
      throw new Error('Invalid or overlapping source clips.');
    }
  }
  const cuts = [];
  let cursor = 0;
  for (const part of sourceOrder) {
    if (part.start > cursor) cuts.push({start: cursor, end: part.start});
    cursor = part.end;
  }
  if (cursor < project.duration) cuts.push({start: cursor, end: project.duration});
  return {...project, clips, cuts};
}

export function removeSourceRange(project, start, end) {
  return withSequence(project, sequenceClips(project).flatMap(part => {
    if (part.end <= start || part.start >= end) return [part];
    const pieces = [];
    if (part.start < start) pieces.push({...part, end: start});
    if (part.end > end) pieces.push({...part, start: end, id: `${part.id}-after-${end}`});
    return pieces;
  }));
}

export function restoreSourceRange(project, start, end) {
  let clips = [...sequenceClips(project)];
  // Only insert missing media; preserve existing clip boundaries and order.
  const missing = normalizeCuts(project.cuts, project.duration)
    .map(cut => ({start: Math.max(start, cut.start), end: Math.min(end, cut.end)}))
    .filter(part => part.end > part.start);
  for (const part of missing) {
    const before = [...clips].filter(clip => clip.end <= part.start).sort((a, b) => b.end - a.end)[0];
    const after = [...clips].filter(clip => clip.start >= part.end).sort((a, b) => a.start - b.start)[0];
    const index = before ? clips.findIndex(clip => clip.id === before.id) + 1 : after ? clips.findIndex(clip => clip.id === after.id) : clips.length;
    clips.splice(index, 0, {...part, id: `restored-${part.start}-${part.end}`});
  }
  return withSequence(project, clips);
}

export function trimClip(project, id, edge, value) {
  const clips = sequenceClips(project);
  const part = clips.find(clip => clip.id === id);
  if (!part || !['start', 'end'].includes(edge) || !Number.isFinite(value)) return project;
  const frameStep = project.frameStatus === 'detected-cfr' || project.frameStatus === 'confirmed-cfr' || project.frameStatus === 'demo-cfr'
    ? project.fpsDen / project.fpsNum : 0.01;
  const step = Number.isFinite(frameStep) && frameStep > 0 ? frameStep : 0.01;
  const others = clips.filter(clip => clip.id !== id);
  const lower = Math.max(0, ...others.filter(clip => clip.end <= part.start).map(clip => clip.end));
  const upper = Math.min(project.duration, ...others.filter(clip => clip.start >= part.end).map(clip => clip.start));
  const snapped = Math.round(value / step) * step;
  const boundary = edge === 'start'
    ? clamp(snapped, lower, Math.max(lower, part.end - step))
    : clamp(snapped, Math.min(upper, part.start + step), upper);
  return withSequence(project, clips.map(clip => clip.id === id ? {...clip, [edge]: boundary} : clip));
}

export function moveClip(project, from, to) {
  const clips = [...sequenceClips(project)];
  if (from < 0 || from >= clips.length || to < 0 || to >= clips.length || from === to) return project;
  clips.splice(to, 0, clips.splice(from, 1)[0]);
  return withSequence(project, clips);
}

export function replaceCuts(project, cuts) {
  const desired = normalizeCuts(cuts, project.duration);
  let next = project;
  for (const part of retainedIntervals(desired, project.duration)) next = restoreSourceRange(next, part.start, part.end);
  for (const cut of desired) next = removeSourceRange(next, cut.start, cut.end);
  return next;
}
